import { useDispatch } from 'react-redux';
import { useRef, useState, useEffect, useCallback } from 'react';

import {
  setChartDataIdx,
  setChartsDataSourceIdx
} from 'store/portCoData/portCoData.slice';
import {
  setHeadCount,
  setRequiredInfo,
  setGrowthAndRetention,
  setOperationalEfficiency
} from 'store/companyInfo/companyInfo.slice';
import DownIcon from 'assets/icons/down.svg';
import helpIcon from 'assets/icons/help.svg';
import RadioIcon from 'assets/icons/radio.svg';
import CustomTooltip from 'components/Tooltip';
import { RootState, useAppSelector } from 'store';
import DarkDownIcon from 'assets/icons/down-white.svg';
import RadioBlueIcon from 'assets/icons/radio-blue.svg';
import BlackTickIcon from 'assets/icons/black_tick.svg';
import RadioGreenIcon from 'assets/icons/radio-green.svg';
import useClickOutside from 'components/ModalTextField/ColorPicker/useClickOutside';

const ChartQuarterOptions = () => {
  const popover = useRef(null);
  const dispatch = useDispatch();
  const [isOpen, toggle] = useState(false);

  const close = useCallback(() => toggle(false), []);

  useClickOutside(popover, close);

  const {
    chartsData: data,
    chartDataIdx: dataIdx,
    chartsDataSourceIdx: sourceIdx
  } = useAppSelector((state: RootState) => state.portCoData);

  const labels = data
    ?.slice(0, data.length < 5 ? data.length - 1 : 4)
    .map((d: any) => d?.label);

  useEffect(() => {
    dispatch(setHeadCount(data?.[dataIdx]?.headCount));
    dispatch(setRequiredInfo(data?.[dataIdx]?.requiredInfo));
    dispatch(setGrowthAndRetention(data[dataIdx]?.growthAndRetention));
    dispatch(setOperationalEfficiency(data?.[dataIdx]?.operationalEfficiency));
  }, [data, dataIdx, dispatch]);

  return (
    <>
      <div
        onClick={() => toggle(true)}
        className={`flex items-center justify-between gap-2 w-[200px] h-10 px-4 border border-gray-300 rounded-full cursor-pointer select-none${
          isOpen && dataIdx === 4 ? ' border-black !dark:border-white' : ''
        }${
          sourceIdx === 1 && dataIdx !== 4
            ? ' bg-[#3333B11A] border-[#3333B133] text-[#3333B1]'
            : ''
        } dark:bg-black !dark:border-white`}
      >
        {sourceIdx === 0 || dataIdx === 4
          ? 'Benchmark Data'
          : `Your Data: ${data?.[dataIdx]?.label}`}

        <picture className={isOpen ? 'rotate-180' : ''}>
          <source srcSet={DarkDownIcon} media='(prefers-color-scheme: dark)' />

          <img src={DownIcon} alt='down' />
        </picture>
      </div>

      {isOpen && (
        <div
          ref={popover}
          className='absolute top-11 left-0 z-[200] py-2 bg-white dark:bg-black rounded-lg border border-gray-300 shadow-popover'
        >
          <div
            onClick={() => {
              close();
              dispatch(setChartDataIdx(4));
              dispatch(setChartsDataSourceIdx(0));
            }}
            className='flex items-center gap-2 px-4 py-2 select-none cursor-pointer'
          >
            <img
              alt='benchmark data'
              src={sourceIdx === 0 ? RadioGreenIcon : RadioIcon}
            />

            <p className='font-light text-lg'>Benchmark Data</p>
          </div>

          <div
            onClick={() => {
              dispatch(setChartsDataSourceIdx(1));
            }}
            className='flex items-center gap-2 px-4 py-2 select-none cursor-pointer'
          >
            <img
              alt='your data'
              src={sourceIdx === 1 ? RadioBlueIcon : RadioIcon}
            />

            <p className='font-light text-lg'>Your Data</p>

            <CustomTooltip
              id='chartInfo'
              icon={helpIcon}
              content='Toggle different quarter end data to view your company data with our benchmarks (fiscal quarter ends have been standardized to fit calendar year labels)'
            />
          </div>

          {sourceIdx === 1 &&
            labels.map((l: string, index: number) => (
              <div
                key={l}
                onClick={() => {
                  close();
                  dispatch(setChartDataIdx(index));
                }}
                className={`relative pl-11 pr-4 py-2 hover:bg-gray-200 text-black dark:text-white dark:hover:text-black cursor-pointer${
                  index === dataIdx ? ' bg-gray-200 !text-black' : ''
                }`}
              >
                {index === dataIdx && (
                  <img
                    alt='checked'
                    src={BlackTickIcon}
                    className='w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2'
                  />
                )}
                {l}
              </div>
            ))}
        </div>
      )}
    </>
  );
};

export default ChartQuarterOptions;
